import { Component, ErrorInfo, ReactNode } from "react";
import { AlertTriangle, RotateCw } from "lucide-react";

interface Props {
  children: ReactNode;
}
interface State {
  hasError: boolean;
  erro?: Error;
}

/**
 * Captura erros de runtime na árvore de componentes e mostra um aviso com botão
 * de recarregar — em vez de deixar a tela BRANCA sem saída (num erro não tratado
 * o React desmonta a árvore inteira).
 *
 * Motivado por um episódio (jul/2026): uma queda momentânea do backend deixou
 * componentes que assumiam dados carregados quebrarem, e vários usuários ficaram
 * presos numa tela branca até um Ctrl+Shift+R manual. Com o boundary, o mesmo
 * cenário vira um aviso amigável com botão de recarregar.
 */
export class ErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false };

  static getDerivedStateFromError(erro: Error): State {
    return { hasError: true, erro };
  }

  componentDidCatch(erro: Error, info: ErrorInfo) {
    // O logger do front é só console (sem transporte de arquivo) — registra aqui
    // pra facilitar a depuração se alguém abrir o DevTools.
    console.error("[ErrorBoundary] erro de renderização:", erro, info.componentStack);
  }

  handleReload = () => {
    window.location.reload();
  };

  render() {
    if (!this.state.hasError) return this.props.children;

    return (
      <div className="min-h-screen flex items-center justify-center p-6 bg-slate-50 dark:bg-slate-950">
        <div className="max-w-md w-full bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-2xl shadow-sm p-8 text-center">
          <div className="mx-auto w-12 h-12 rounded-full bg-amber-100 dark:bg-amber-900/40 flex items-center justify-center mb-4">
            <AlertTriangle className="text-amber-600 dark:text-amber-400" size={24} />
          </div>
          <h1 className="text-lg font-semibold text-slate-800 dark:text-slate-100">
            Algo deu errado ao carregar a tela
          </h1>
          <p className="text-sm text-slate-500 dark:text-slate-400 mt-2">
            Isso costuma acontecer depois de uma instabilidade momentânea da conexão.
            Recarregar a página normalmente resolve.
          </p>
          <button
            onClick={this.handleReload}
            className="mt-5 inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium w-full transition-colors"
          >
            <RotateCw size={16} /> Recarregar página
          </button>
          <p className="text-[11px] text-slate-400 dark:text-slate-500 mt-3">
            Se continuar, tente Ctrl + Shift + R ou avise o suporte.
          </p>
        </div>
      </div>
    );
  }
}
